import { Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { FontSizes, IconButton, useClassNames, useTheme } from '@h2oai/ui-kit';
import { useCallback } from 'react';

import { GrpcStatusCode } from '../../../services/HTTPResponse';
import { formatDefaultDateAndTimeWithSeconds } from '../../../utils/utils';
import { AuditTrailListRowClassNames, AuditTrailListRowStyles, auditTrailListRowStyles } from '../auditTrail.styles';
import { AuditTrailListRowProps } from '../types';
import { LogLabel, LogText } from './Parts';

const getContent = (rawContent: any, formatDate = true): string => {
  if (rawContent === undefined) return 'N/A';

  if (rawContent === '') return '""';

  if (formatDate) {
    if (typeof rawContent === 'string' && !isNaN(Date.parse(rawContent))) {
      return formatDefaultDateAndTimeWithSeconds(rawContent);
    }
  }

  // Handle gRPC status codes
  if (typeof rawContent === 'number') {
    const statusCodeName = GrpcStatusCode[rawContent];
    if (statusCodeName) return statusCodeName;
    else return rawContent.toString();
  }

  return String(rawContent);
};

export const AuditTrailListRow = ({ event, header, columns }: AuditTrailListRowProps) => {
  const theme = useTheme();
  const { palette, semanticColors } = theme;
  const [expanded, { toggle: toggleExpand }] = useBoolean(false);
  const baseTextProps = { expanded, header };
  const classNames = useClassNames<Partial<AuditTrailListRowStyles>, AuditTrailListRowClassNames>(
    'LogRow',
    auditTrailListRowStyles(theme, expanded, header)
  );

  const handleCopyEvent = useCallback(() => {
    if (event) {
      try {
        const eventJson = JSON.stringify(event, null, 2);
        navigator.clipboard.writeText(eventJson).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = eventJson;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        });
      } catch (error) {
        // Silent fail - don't log errors to console
      }
    }
  }, [event]);

  return (
    <>
      <tr className={classNames.root}>
        <td>
          {expanded && (
            <IconButton
              styles={{
                root: {
                  backgroundColor: palette?.gray800,
                  '&:not(:disabled):hover': { backgroundColor: palette?.gray700 },
                },
                icon: {
                  fontSize: FontSizes.xsmall,
                  height: 18,
                  lineHeight: 18,
                  color: semanticColors?.buttonPrimaryDisabledBorder,
                },
              }}
              iconName="Copy"
              onClick={handleCopyEvent}
              title="Copy event to clipboard"
            />
          )}
        </td>
        <td style={{ width: 20 }}>
          <Stack styles={{ root: { minWidth: 20, maxWidth: 20 } }}>
            {!header && (
              <IconButton
                styles={{
                  root: {
                    backgroundColor: palette?.gray800,
                    '&.is-checked': {
                      '.ms-Icon': {
                        color: 'white',
                      },
                    },
                    '&:not(:disabled):hover': {
                      backgroundColor: palette?.gray700,
                    },
                  },
                  icon: {
                    fontSize: FontSizes.xsmall,
                    height: 18,
                    lineHeight: 18,
                    color: semanticColors?.buttonPrimaryDisabledBorder,
                  },
                }}
                iconName={expanded ? `ChevronDown` : `ChevronRight`}
                onClick={toggleExpand}
              />
            )}
          </Stack>
        </td>

        {columns.map((column, i) => (
          <td key={i}>
            {header ? (
              <LogText content={column} {...baseTextProps} />
            ) : (
              <LogText content={event && getContent((event as any)[column])} {...baseTextProps} />
            )}
          </td>
        ))}
      </tr>

      {expanded && event && (
        <>
          {Object.entries(event).map(([key, value]) => (
            <tr key={key} className={classNames.expandable}>
              <td></td>
              <td></td>
              <td colSpan={columns.length}>
                <Stack horizontal className={classNames.logLine}>
                  <LogLabel content={`${key}: `} />
                  <LogText content={getContent(value, false)} {...baseTextProps} truncate />
                </Stack>
              </td>
            </tr>
          ))}
        </>
      )}
    </>
  );
};
